import { FC } from 'react';
import { Box, Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import Button from '@/components/button';
import Card from '@/components/card';
import CardHeader from '@/components/card/card-header';
import NoneChip from '@/components/chip/none-chip';
import Text from '@/components/text';
import useModal from '@/hooks/use-modal.hook';
import useToast from '@/hooks/use-toast.hook';
import { OrgModel, PolicyEngineMergeStrategyOptions } from '@/lib/models/org.model';
import { Props as ConversionProps } from '@/components/modals/policy-conversion';
import { Props as RevertProps } from '@/components/modals/policy-engine-revert';

interface Props {
  org: OrgModel;
  onChange: () => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  policyEngineCard: {
    padding: 10,
    borderLeft: `4px solid ${theme.palette.gray}`,
  },
}));

// Root only. The caller must not render it for a partner user.
const PolicyEngineCard: FC<Props> = ({ org, onChange }) => {
  const { openModal } = useModal();
  const { toast } = useToast();
  const styles = useStyles();
  const { id, name, policyEngine } = org;
  const onV1 = policyEngine.version === 'v1';

  const MergeStrategyEl = onV1 ? (
    <NoneChip notAvailable />
  ) : (
    PolicyEngineMergeStrategyOptions.find(({ value }) => value === policyEngine.mergeStrategy)
      ?.label
  );

  const handleDryRun = () => {
    openModal<ConversionProps>('policy-conversion', { orgId: id, orgName: name, dryRunOnly: true });
  };

  const handleSwitchToV2 = () => {
    openModal<ConversionProps>('policy-conversion', {
      orgId: id,
      orgName: name,
      onSwitched: () => {
        toast('The organization now uses policy engine v2');
        onChange();
      },
    });
  };

  const handleSwitchToV1 = () => {
    openModal<RevertProps>('policy-engine-revert', {
      orgId: id,
      orgName: name,
      policyEngine,
      onReverted: () => {
        toast('The organization now uses policy engine v1');
        onChange();
      },
    });
  };

  return (
    <Card>
      <CardHeader title="Policy Engine" icon="policy">
        <Stack direction="row" gap={1}>
          {onV1 && <Button label="Dry Run" type="option" size="small" onClick={handleDryRun} />}
          <Button
            label={onV1 ? 'Switch to v2' : 'Switch Back to v1'}
            size="small"
            onClick={onV1 ? handleSwitchToV2 : handleSwitchToV1}
          />
        </Stack>
      </CardHeader>
      <Box width="100%">
        <Box className={styles.policyEngineCard} width="100%" mb={2}>
          <Stack direction="row" justifyContent="space-between">
            <Box width="50%">
              <Text bold inline>
                Version:
              </Text>{' '}
              {policyEngine.version}
            </Box>
            <Box width="50%">
              <Text bold inline>
                Merge Strategy:
              </Text>{' '}
              {MergeStrategyEl}
            </Box>
          </Stack>
        </Box>
      </Box>
    </Card>
  );
};

export default PolicyEngineCard;
